import React, { useRef } from "react"
import PropTypes from "prop-types"
import CustomButton from "../../../components/OnboardingComponents/CustomButton"
import CustomInput from "../../../components/OnboardingComponents/CustomInput"
import { getUserFullName } from "../../../helpers/userDisplay"
import ProfilePhoto from "../../Profile/ProfilePhoto"

const MAX_PHOTO_BYTES = 5120 * 1024

const ACCEPTED_PHOTO_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/gif"]

const isAcceptedPhoto = file => {
  const mime = String(file?.type || "").toLowerCase()
  if (ACCEPTED_PHOTO_TYPES.includes(mime)) return true
  const name = String(file?.name || "").toLowerCase()
  return /\.(jpe?g|png|gif)$/.test(name)
}

const ProfileSettingsTab = ({
  profile,
  onChange,
  photoPreview,
  onPhotoChange,
  onManageTwoFactor,
  twoFactorLoading = false,
  emailDisabled = false,
  fieldErrors = {},
}) => {
  const fileInputRef = useRef(null)

  const handlePhotoSelect = event => {
    const file = event.target.files?.[0]
    if (!file) return

    if (!isAcceptedPhoto(file)) {
      onPhotoChange(null, "Invalid file type. Please upload a JPG, GIF, or PNG.")
      event.target.value = ""
      return
    }

    if (file.size > MAX_PHOTO_BYTES) {
      onPhotoChange(null, "Photo must be 5MB or smaller.")
      event.target.value = ""
      return
    }

    onPhotoChange(file)
    event.target.value = ""
  }

  return (
    <div className="admin-settings__panel">
      <section className="admin-settings__photo-section">
        <div className="admin-settings__photo-row">
          <ProfilePhoto
            src={photoPreview || null}
            name={getUserFullName({
              first_name: profile.firstName,
              last_name: profile.lastName,
            })}
            className="admin-settings__photo"
          />
          <div className="admin-settings__photo-actions">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/gif,.jpg,.jpeg,.png,.gif"
              className="admin-settings__file-input"
              onChange={handlePhotoSelect}
            />
            <CustomButton
              variant="secondary"
              size="sm"
              type="button"
              onClick={() => fileInputRef.current?.click()}
            >
              Change Photo
            </CustomButton>
            <p className="admin-settings__photo-hint">
              JPG, GIF or PNG. Max size of 5MB
            </p>
            {fieldErrors.photo ? (
              <p className="amok-field__message amok-field__message--error">
                {fieldErrors.photo}
              </p>
            ) : null}
          </div>
        </div>
      </section>

      <div className="admin-settings__fields">
        <div className="admin-settings__row admin-settings__row--two">
          <CustomInput
            label="First Name"
            name="firstName"
            placeholder="Enter first name"
            value={profile.firstName}
            onChange={event => onChange("firstName", event.target.value)}
            error={fieldErrors.firstName}
          />
          <CustomInput
            label="Last Name"
            name="lastName"
            placeholder="Enter last name"
            value={profile.lastName}
            onChange={event => onChange("lastName", event.target.value)}
            error={fieldErrors.lastName}
          />
        </div>

        <div className="admin-settings__row admin-settings__row--two">
          <CustomInput
            label="Email Address"
            name="email"
            type="email"
            placeholder="Enter email address"
            value={profile.email}
            onChange={event => onChange("email", event.target.value)}
            disabled={emailDisabled}
            readOnly={emailDisabled}
            autoComplete="email"
            className={
              emailDisabled ? "admin-settings__field--disabled" : undefined
            }
          />
          <CustomInput
            label="Phone Number"
            name="phone"
            placeholder="Enter phone number"
            value={profile.phone}
            onChange={event => onChange("phone", event.target.value)}
            error={fieldErrors.phone}
          />
        </div>

        <div className="admin-settings__twofa-card">
          <div className="admin-settings__twofa-copy">
            <p className="admin-settings__twofa-title">
              Two-Factor Authentication (2FA)
            </p>
            <div className="admin-settings__twofa-status">
              <span className="admin-settings__twofa-label">2FA Status</span>
              <span
                className={`admin-settings__status-badge${
                  profile.twoFactorEnabled
                    ? " admin-settings__status-badge--enabled"
                    : " admin-settings__status-badge--disabled"
                }`}
              >
                <span className="admin-settings__status-dot" aria-hidden="true" />
                {profile.twoFactorEnabled ? "Enabled" : "Disabled"}
              </span>
            </div>
          </div>
          <CustomButton
            variant="secondary"
            size="md"
            type="button"
            onClick={onManageTwoFactor}
            loading={twoFactorLoading}
            disabled={twoFactorLoading}
          >
            {profile.twoFactorEnabled ? "Disable 2FA" : "Enable 2FA"}
          </CustomButton>
        </div>
      </div>
    </div>
  )
}

ProfileSettingsTab.propTypes = {
  profile: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    email: PropTypes.string,
    phone: PropTypes.string,
    twoFactorEnabled: PropTypes.bool,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  photoPreview: PropTypes.string,
  onPhotoChange: PropTypes.func.isRequired,
  onManageTwoFactor: PropTypes.func.isRequired,
  twoFactorLoading: PropTypes.bool,
  emailDisabled: PropTypes.bool,
  fieldErrors: PropTypes.object,
}

export default ProfileSettingsTab
